/**
 * Edit form of a submitted registration (ADB-169). One component for both ways in:
 *   - mode "link":  the participant, through a personal edit link on the public site
 *   - mode "admin": staff, inside the admin app
 * What can be edited follows the public registration form (see registrationEditLogic.ts); the
 * database applies the same rules. The CI and a minister's district, region, degree and position
 * are shown read-only.
 */

import { useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { DocumentUploadField } from './DocumentUploadField';
import { ExistingDocumentPreview } from './ExistingDocumentPreview';
import { MinisterLookup } from './MinisterLookup';
import { SignaturePad } from './SignaturePad';
import {
  buildEditPayload,
  editableFields,
  hasChanges,
  initialEditValues,
  missingForSave,
  type EditFormValues,
} from '../registrationEditLogic';
import {
  DEFAULT_DECLARATION,
  type DocumentKind,
  type EditableFormKey,
  type RegistrationEditPayload,
  type RegistrationEditView,
} from '../types';

interface RegistrationEditFormProps {
  view: RegistrationEditView;
  mode: 'link' | 'admin';
  /** Uploads a file and returns its storage key (public edit upload or staff upload) */
  upload: (kind: DocumentKind, file: Blob) => Promise<string>;
  /** Signed link of a document already uploaded (shown as a thumbnail). Omit to show none. */
  loadDocument?: (
    kind: DocumentKind,
  ) => Promise<{ url: string; isPdf: boolean } | null>;
  /** Whose documents these are (token or participant id): keeps cached previews apart */
  cacheKey: string;
  saving: boolean;
  error?: string | null;
  onSave: (payload: RegistrationEditPayload) => void;
  onCancel?: () => void;
}

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}

function ReadOnly({ value }: { value: string | null | undefined }) {
  return (
    <Input
      value={value || '—'}
      readOnly
      disabled
      className="disabled:cursor-default disabled:opacity-100"
    />
  );
}

const DOC_LABELS: Record<Exclude<DocumentKind, 'signature'>, string> = {
  ci_front: 'Cédula de identidad — anverso',
  ci_back: 'Cédula de identidad — reverso',
  voucher: 'Comprobante de pago',
  delegation_letter: 'Carta de delegación',
};

export function RegistrationEditForm({
  view,
  mode,
  upload,
  loadDocument,
  cacheKey,
  saving,
  error,
  onSave,
  onCancel,
}: RegistrationEditFormProps) {
  const kind = view.kind;
  const event = view.event;
  const form = view.form as Record<string, string | undefined>;
  const editable = useMemo(
    () => editableFields(kind, event.registration_form_type),
    [kind, event.registration_form_type],
  );
  const initial = useMemo(() => initialEditValues(view), [view]);

  const [values, setValues] = useState<EditFormValues>(initial);
  const [docs, setDocs] = useState<RegistrationEditPayload['documents']>({});
  const [signature, setSignature] = useState<Blob | null>(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Delegate: who they represent (a registered minister, or free text)
  const [repMode, setRepMode] = useState<'minister' | 'search' | 'manual'>(
    initial.represented_minister_id ? 'minister' : 'manual',
  );
  const [repLabel, setRepLabel] = useState<string | null>(
    initial.represented_minister_id
      ? `${form.represented_name ?? ''}${form.represented_adb_code ? ` (${form.represented_adb_code})` : ''}`
      : null,
  );

  const set = (key: EditableFormKey, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));
  const can = (key: EditableFormKey) => editable.has(key);

  const isDigital = event.signature_mode === 'DIGITAL';
  const alreadySigned = view.documents.signature;
  const signatureRequired = mode === 'link' && isDigital && !alreadySigned;

  const regions = useMemo(
    () =>
      values.district_id
        ? view.regions.filter((r) => r.district_id === values.district_id)
        : [],
    [view.regions, values.district_id],
  );

  const missing = missingForSave(kind, values, editable, {
    required: signatureRequired,
    present: !!signature,
  });
  const documentList: Exclude<DocumentKind, 'signature'>[] =
    kind === 'DELEGATE'
      ? ['delegation_letter', 'voucher']
      : ['ci_front', 'ci_back', 'voucher'];

  const payloadPreview = buildEditPayload(initial, values, editable, docs);
  const dirty = hasChanges(payloadPreview) || !!signature;
  const busy = saving || uploadingSignature;

  const handleSave = async () => {
    setLocalError(null);
    let documents = docs;
    if (signature) {
      setUploadingSignature(true);
      try {
        documents = {
          ...docs,
          signature: await upload('signature', signature),
        };
      } catch (err) {
        setLocalError(
          err instanceof Error ? err.message : 'No se pudo subir la firma',
        );
        return;
      } finally {
        setUploadingSignature(false);
      }
    }
    onSave(buildEditPayload(initial, values, editable, documents));
  };

  const documentsNote =
    mode === 'link' && view.registration_status === 'VERIFIED'
      ? 'Si cambia un documento, la comisión organizadora volverá a revisar su inscripción.'
      : null;

  return (
    <div className="space-y-4">
      {/* Data */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Datos personales</CardTitle>
          {kind === 'MINISTER' && (
            <p className="text-xs text-muted-foreground">
              Distrito, región, grado y cargo provienen del registro ministerial
              y no se pueden modificar aquí.
            </p>
          )}
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Nombre completo" required className="sm:col-span-2">
            <Input
              value={values.full_name}
              onChange={(e) => set('full_name', e.target.value)}
            />
          </Field>
          <Field label="Nº Cédula de identidad">
            <ReadOnly value={form.dni} />
          </Field>

          {kind === 'MINISTER' && (
            <>
              <Field label="Distrito">
                <ReadOnly value={form.district_name} />
              </Field>
              <Field label="Región">
                <ReadOnly value={form.region_name} />
              </Field>
              {event.registration_form_type === 'FULL_CONVENTION' && (
                <>
                  <Field label="Grado ministerial">
                    <ReadOnly value={form.degree_name} />
                  </Field>
                  <Field label="Cargo">
                    <ReadOnly value={form.position} />
                  </Field>
                </>
              )}
              {can('birth_date') && (
                <Field label="Fecha de nacimiento">
                  <Input
                    type="date"
                    value={values.birth_date}
                    onChange={(e) => set('birth_date', e.target.value)}
                  />
                </Field>
              )}
            </>
          )}

          {can('district_id') && (
            <>
              <Field label="Distrito">
                <Select
                  value={values.district_id || undefined}
                  onValueChange={(v) =>
                    setValues((p) => ({ ...p, district_id: v, region_id: '' }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione" />
                  </SelectTrigger>
                  <SelectContent>
                    {view.districts.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Región">
                <Select
                  value={values.region_id || undefined}
                  onValueChange={(v) => set('region_id', v)}
                  disabled={!values.district_id}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        values.district_id ? 'Seleccione' : 'Elija un distrito'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </>
          )}

          {can('position') && (
            <Field label="Cargo">
              <Input
                value={values.position}
                onChange={(e) => set('position', e.target.value)}
              />
            </Field>
          )}

          <Field label="Celular" required>
            <Input
              type="tel"
              inputMode="tel"
              value={values.cell_phone}
              onChange={(e) => set('cell_phone', e.target.value)}
            />
          </Field>
          {can('office_phone') && (
            <Field label="Teléfono de oficina">
              <Input
                type="tel"
                value={values.office_phone}
                onChange={(e) => set('office_phone', e.target.value)}
              />
            </Field>
          )}
          <Field label="E-mail">
            <Input
              type="email"
              value={values.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="Opcional"
            />
          </Field>
          {can('address') && (
            <Field
              label="Dirección para envío de correspondencia"
              className="sm:col-span-2"
            >
              <Input
                value={values.address}
                onChange={(e) => set('address', e.target.value)}
              />
            </Field>
          )}

          {kind === 'DELEGATE' && (
            <div className="space-y-3 sm:col-span-2">
              <Label>
                ¿A quién representa? <span className="text-destructive">*</span>
              </Label>
              {repMode === 'minister' && repLabel && (
                <div className="flex items-center justify-between rounded-md border p-3 text-sm">
                  <span>{repLabel}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setRepMode('search')}
                  >
                    Cambiar
                  </Button>
                </div>
              )}
              {repMode === 'search' && (
                <div className="space-y-2">
                  <MinisterLookup
                    eventId={view.event_id}
                    placeholder="Nombre, CI o código del ministro que representa"
                    onSelect={(m) => {
                      setValues((p) => ({
                        ...p,
                        represented_minister_id: m.id,
                        represented_name: '',
                        represented_ci: '',
                        institution_note: '',
                      }));
                      setRepLabel(`${m.full_name} (${m.adb_code})`);
                      setRepMode('minister');
                    }}
                  />
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0"
                    onClick={() => setRepMode('manual')}
                  >
                    Representa a una iglesia, institución u otra persona
                  </Button>
                </div>
              )}
              {repMode === 'manual' && (
                <div className="space-y-3">
                  <Field
                    label="Nombre de la persona, iglesia o institución que representa"
                    required
                  >
                    <Input
                      value={values.represented_name}
                      onChange={(e) =>
                        setValues((p) => ({
                          ...p,
                          represented_minister_id: '',
                          represented_name: e.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field label="Nº de CI del pastor / representante legal">
                    <Input
                      value={values.represented_ci}
                      onChange={(e) => set('represented_ci', e.target.value)}
                    />
                  </Field>
                  <Field label="Detalle (ej. Exploradores — Región La Paz)">
                    <Input
                      value={values.institution_note}
                      onChange={(e) => set('institution_note', e.target.value)}
                    />
                  </Field>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0"
                    onClick={() => setRepMode('search')}
                  >
                    Buscar ministro en su lugar
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Documentos</CardTitle>
          {documentsNote && (
            <p className="text-xs text-muted-foreground">{documentsNote}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {documentList.map((doc) => (
            <div key={doc} className="space-y-2">
              {view.documents[doc] && !docs[doc] && loadDocument && (
                <ExistingDocumentPreview
                  kind={doc}
                  cacheKey={cacheKey}
                  load={loadDocument}
                />
              )}
              <DocumentUploadField
                eventId={view.event_id}
                kind={doc}
                label={DOC_LABELS[doc]}
                hint={
                  view.documents[doc]
                    ? 'Ya está cargado. Suba otro archivo solo si quiere reemplazarlo.'
                    : 'Aún no está cargado.'
                }
                value={docs[doc]}
                onChange={(key) =>
                  setDocs((d) => {
                    const next = { ...d };
                    if (key) next[doc] = key;
                    else delete next[doc];
                    return next;
                  })
                }
                upload={upload}
                disabled={busy}
                optional
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Signature */}
      {isDigital && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Firma</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm italic text-muted-foreground">
              {event.registration_declaration || DEFAULT_DECLARATION}
            </p>
            {alreadySigned && loadDocument && (
              <ExistingDocumentPreview
                kind="signature"
                cacheKey={cacheKey}
                load={loadDocument}
              />
            )}
            <div className="space-y-2">
              <Label>
                {alreadySigned ? 'Firmar de nuevo (opcional)' : 'Firma'}{' '}
                {signatureRequired && (
                  <span className="text-destructive">*</span>
                )}
              </Label>
              <SignaturePad onChange={setSignature} disabled={busy} />
              <p className="text-xs text-muted-foreground">
                {signatureRequired
                  ? 'Dibuje su firma con el dedo o el mouse. Es obligatoria para guardar.'
                  : alreadySigned
                    ? 'Su formulario ya está firmado. Dibuje una firma nueva solo si quiere reemplazar la anterior.'
                    : 'Dibuje la firma con el dedo o el mouse.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {(error || localError) && (
        <Alert variant="destructive">
          <AlertDescription>{error || localError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        {missing.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Falta completar: {missing.join(', ')}.
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={busy}
            >
              <X className="mr-2 size-4" />
              Cancelar
            </Button>
          )}
          <Button
            type="button"
            size="lg"
            onClick={handleSave}
            disabled={busy || missing.length > 0 || !dirty}
          >
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
}
